import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { getShMeta } from '../../api/shMeta.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface DelegationRow {
  readonly delegationId: string;
  readonly delegatorName: string | null;
  readonly delegatorCode: string | null;
  readonly delegateName: string | null;
  readonly delegateCode: string | null;
  readonly roleCode: string;
  readonly status: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly territoryCodes: string[] | null;
  readonly budgetLimitInr: number | null;
  readonly revocationReason: string | null;
  readonly createdAt: string;
}


export function ShDelegationPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const [delegated, setDelegated] = useState<DelegationRow[]>([]);
  const [received, setReceived] = useState<DelegationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'delegated' | 'received'>('delegated');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [delegateTo, setDelegateTo] = useState('');
  const [roleCode, setRoleCode] = useState('');
  const [delegableRoles, setDelegableRoles] = useState<readonly string[]>([]);
  // Kept apart from `error`, which every list reload clears: without the meta
  // there is no governed role to delegate, and that must stay said.
  const [metaError, setMetaError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [territories, setTerritories] = useState('');
  const [budgetLimit, setBudgetLimit] = useState('');

  // Revoke form
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([
        apiRequest<DelegationRow[]>('/v1/sh/delegation/delegator', { token }),
        apiRequest<DelegationRow[]>('/v1/sh/delegation/delegate', { token }),
      ]);
      setDelegated(d); setReceived(r); setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    let alive = true;
    getShMeta(token)
      .then((m) => {
        if (!alive) return;
        setDelegableRoles(m.delegableRoles);
        setRoleCode((prev) => prev || m.delegableRoles[0] || '');
      })
      .catch((e) => { if (alive) setMetaError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const handleCreate = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy || metaError || !roleCode) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const body: Record<string, unknown> = {
        delegateUserId: delegateTo, roleCode,
        effectiveFromDate: fromDate, effectiveToDate: toDate,
      };
      if (territories.trim()) body.territoryCodes = territories.split(',').map((s) => s.trim());
      if (budgetLimit) body.budgetLimitInr = Number(budgetLimit);
      await apiRequest('/v1/sh/delegation', { method: 'POST', token, body });
      setInfo(t('sh.delegation.created'));
      setShowCreate(false); setDelegateTo(''); setFromDate(''); setToDate(''); setTerritories(''); setBudgetLimit('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [delegateTo, roleCode, fromDate, toDate, territories, budgetLimit, token, busy, load, metaError]);

  const handleRevoke = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!revokeId || busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/sh/delegation/${encodeURIComponent(revokeId)}/revoke`, {
        method: 'POST', token, body: { revocationReason: revokeReason },
      });
      setInfo(t('sh.delegation.revoked'));
      setRevokeId(null); setRevokeReason('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [revokeId, revokeReason, token, busy, load]);

  const activeRows = tab === 'delegated' ? delegated : received;

  return (
    <main aria-label="sh-delegation">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/sh/dashboard" data-testid="back-dashboard">{t('sh.common.dashboard')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.delegation.title')}</h1>
        <Button type="button" variant="secondary" onClick={() => setShowCreate(!showCreate)} data-testid="btn-new-delegation"
                style={{ marginLeft: 'auto' }}>{t('sh.common.newBtn')}</Button>
      </div>

      {metaError && <p role="alert" data-testid="sh-meta-error" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{metaError}</p>}
      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {/* ── Create form ──────────────────────────────── */}
      {showCreate && (
        <section style={{ padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)' }}
                 aria-label="create-delegation-form">
          <form onSubmit={handleCreate} aria-label="delegation-form">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              <div>
                <label htmlFor="del-to" style={{ fontSize: 11 }}>{t('sh.delegation.delegateTo')}</label>
                <input id="del-to" value={delegateTo} onChange={(e) => setDelegateTo(e.target.value)} required
                       data-testid="input-del-to"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="del-role" style={{ fontSize: 11 }}>{t('sh.common.role')}</label>
                <select id="del-role" value={roleCode} onChange={(e) => setRoleCode(e.target.value)} data-testid="select-del-role"
                        style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                  {delegableRoles.map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="del-from" style={{ fontSize: 11 }}>{t('sh.delegation.from')}</label>
                <DateInput id="del-from" type="date" value={fromDate} onValueChange={(nextValue) => setFromDate(nextValue)} required
                       data-testid="input-del-from"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="del-to-date" style={{ fontSize: 11 }}>{t('sh.delegation.to')}</label>
                <DateInput id="del-to-date" type="date" value={toDate} onValueChange={(nextValue) => setToDate(nextValue)} required
                       data-testid="input-del-to-date"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="del-terr" style={{ fontSize: 11 }}>{t('sh.delegation.territoriesLabel')}</label>
                <input id="del-terr" value={territories} onChange={(e) => setTerritories(e.target.value)}
                       data-testid="input-del-territories" placeholder="WEST-001, WEST-002"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="del-limit" style={{ fontSize: 11 }}>{t('sh.delegation.budgetLimit')}</label>
                <input id="del-limit" type="number" value={budgetLimit} onChange={(e) => setBudgetLimit(e.target.value)}
                       data-testid="input-del-limit"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Button type="submit" variant="primary" disabled={busy || !!metaError || !roleCode} data-testid="btn-submit-delegation">{t('sh.common.create')}</Button>
              <Button type="button" variant="danger" onClick={() => setShowCreate(false)}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {/* ── Revoke ───────────────────────────────────── */}
      {revokeId && (
        <section style={{ padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)' }}
                 aria-label="revoke-delegation-form">
          <form onSubmit={handleRevoke} aria-label="revoke-form">
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="del-rev" style={{ fontSize: 11 }}>{t('sh.delegation.revocationReason')}</label>
                <input id="del-rev" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} data-testid="input-revoke-reason"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-revoke">{t('sh.delegation.revoke')}</Button>
              <Button type="button" variant="danger" onClick={() => setRevokeId(null)}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {/* ── Tabs ─────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 12 }}>
        <Button type="button" variant={tab === 'delegated' ? 'primary' : 'secondary'} onClick={() => setTab('delegated')} data-testid="tab-delegated">
          {t('sh.delegation.tabMy')}
        </Button>
        <Button type="button" variant={tab === 'received' ? 'primary' : 'secondary'} onClick={() => setTab('received')} data-testid="tab-received">
          {t('sh.delegation.tabReceived')}
        </Button>
      </div>

      {activeRows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.delegation.empty')}</p>
      ) : (
        <DataTable<DelegationRow>
          ariaLabel="delegation-table"
          rows={activeRows}
          rowKey={(r) => r.delegationId}
          columns={[
            { key: 'party', header: tab === 'delegated' ? t('sh.delegation.colDelegate') : t('sh.delegation.colDelegator'), render: (r) => (tab === 'delegated' ? personLabel(r.delegateName, r.delegateCode) : personLabel(r.delegatorName, r.delegatorCode)) ?? '—' },
            { key: 'role', header: t('sh.common.role'), render: (r) => r.roleCode },
            { key: 'status', header: t('sh.common.status'), render: (r) => r.status },
            { key: 'period', header: t('sh.common.period'), render: (r) => <span style={{ fontSize: 12 }}>{day(r.effectiveFromDate)} – {day(r.effectiveToDate)}</span> },
            { key: 'territories', header: t('sh.delegation.colTerritories'), render: (r) => <span style={{ fontSize: 12 }}>{r.territoryCodes?.join(', ') ?? '—'}</span> },
            { key: 'budgetLimit', header: t('sh.delegation.colBudgetLimit'), render: (r) => <>₹{money(r.budgetLimitInr)}</> },
            {
              key: 'actions', header: '',
              render: (r) => (
                r.status === 'ACTIVE' && tab === 'delegated' ? (
                  <Button type="button" variant="danger" onClick={() => { setRevokeId(r.delegationId); setRevokeReason(''); }}
                          data-testid={`btn-revoke-${r.delegationId}`}>{t('sh.delegation.revoke')}</Button>
                ) : null
              ),
            },
          ]}
        />
      )}
    </main>
  );
}
